import { watch } from 'vue'
import type { RouteLocationNormalized, Router } from 'vue-router'
import { useAuthStore, useAppStore, useOrgStore } from '@/stores'
import { createAppApi } from '@/datasources/api/apps'
import i18n from '@/i18n'

const BRAND = 'ServerlessInsight'

/**
 * Every route declares `meta.title` as an i18n key — until now nothing applied
 * it, so the tab always read "ServerlessInsight Console" and those keys were
 * dead data. Set the document title after each navigation and refresh it when
 * the locale changes.
 */
const applyDocumentTitle = (to: RouteLocationNormalized): void => {
  const key = typeof to.meta.title === 'string' ? to.meta.title : ''
  const label = key ? i18n.global.t(key) : ''
  document.title = label ? `${label} · ${BRAND}` : BRAND
}

export const setupGuards = (router: Router): void => {
  router.beforeEach(async (to, _from) => {
    const authStore = useAuthStore()

    // Auth required → redirect to login
    if (to.meta.auth && !authStore.isAuthenticated) {
      return { name: 'login', query: { redirect: to.fullPath } }
    }

    // Platform admins (conf allowlist, role 'admin', e.g. support@wentsen.com)
    // belong to no org by design — the account-level /admin console is home.
    const isPlatformAdmin = (authStore.user?.roles ?? []).includes('admin')

    // Auth pages: ensure orgs are loaded so activeOrg is never silently null
    // (a refresh on an authenticated page would otherwise show "no org selected"
    // because fetchOrgs only ran on the guest→auth transition).
    if (to.meta.auth && authStore.isAuthenticated) {
      const orgStore = useOrgStore()
      if (orgStore.orgs.length === 0) {
        await orgStore.fetchOrgs()
      }
      if (orgStore.orgs.length === 0) {
        if (isPlatformAdmin) {
          // Org-scoped pages have nothing to render without an org.
          if (to.params.appId) {
            return '/admin'
          }
        } else {
          // Onboarding itself is an auth route — redirecting it to itself
          // loops forever for a user with no org yet (any fresh database).
          if (to.name !== 'onboarding') {
            return { name: 'onboarding' }
          }
        }
      }
    }

    // Guest page but already authenticated → redirect to app or onboarding
    if (to.meta.guest && authStore.isAuthenticated) {
      const orgStore = useOrgStore()
      const appStore = useAppStore()

      if (orgStore.orgs.length === 0) {
        await orgStore.fetchOrgs()
      }

      // No org → user hasn't completed onboarding
      if (orgStore.orgs.length === 0) {
        return isPlatformAdmin ? '/admin' : { name: 'onboarding' }
      }

      // Has org → redirect to the remembered (or first) app
      await appStore.fetchApps()

      if (appStore.apps.length === 0) {
        const activeOrg = orgStore.activeOrg || orgStore.orgs[0]
        if (activeOrg) {
          orgStore.switchOrg(activeOrg.id)
          try {
            const res = await createAppApi('default')
            appStore.apps.push(res.data)
            appStore.selectApp(res.data.id)
          } catch {
            return { name: 'login' }
          }
        }
      }

      if (appStore.apps.length > 0) {
        const preferred = appStore.apps.some((a) => a.id === appStore.selectedAppId)
          ? appStore.selectedAppId!
          : appStore.apps[0].id
        return `/${preferred}/services`
      }

      return { name: 'login' }
    }

    // Validate appId param exists in apps list
    if (to.params.appId) {
      const appStore = useAppStore()
      if (appStore.apps.length === 0) {
        await appStore.fetchApps()
      }
      const valid = appStore.apps.some((a) => a.id === to.params.appId)
      if (!valid && appStore.apps.length > 0) {
        const preferred = appStore.apps.some((a) => a.id === appStore.selectedAppId)
          ? appStore.selectedAppId!
          : appStore.apps[0].id
        return `/${preferred}/services`
      }
      if (!valid) {
        return { name: 'login' }
      }
    }
  })

  router.afterEach((to) => applyDocumentTitle(to))
  // A language switch must not leave the old-language title behind.
  watch(i18n.global.locale, () => applyDocumentTitle(router.currentRoute.value))
}
